import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import View from 'ol/View';
import Feature from 'ol/Feature';
import { Color } from 'ol/color';
import { getCenter } from 'ol/extent';
import GeoJSON from 'ol/format/GeoJSON';
import ImageLayer from 'ol/layer/Image';
import { useInterval } from 'usehooks-ts';
import Static from 'ol/source/ImageStatic';
import { MapBrowserEvent, Overlay } from 'ol';
import { Vector as VectorLayer } from 'ol/layer';
import { Polygon, SimpleGeometry } from 'ol/geom';
import { HubConnectionState } from '@microsoft/signalr';
import { fromLonLat, Projection, toLonLat } from 'ol/proj';
import { ImageStatic, Vector as VectorSource } from 'ol/source';
import { sampleSize, difference, cloneDeep, isEqual } from 'lodash';
import { Switch, SwitchChangeEvent } from '@progress/kendo-react-inputs';

import {
  OpenTask,
  NavigateToWorldMap,
  PlayerNavigateToZone,
  UpdatePlayerCoordinate
} from '../../utils/game-engine/player';
import MoveMapPad from './map-move-pad';
import {
  GetZoneById,
  GetZonesByIds,
  GetAreasByZoneId,
  GetAreasWorldMap,
  GetZoneRulesById,
  GetAreaById,
  GetDocumentAreas
} from '../../utils/game-document/assets';
import { MapAdapter } from './mapAdapter';
import { MapContext } from './map-context';
import {
  AreaEntity,
  MapEntity,
  TaskEntity
} from '../../types/game-document/entities';
import MapResetView from './map-reset-view';
import {
  getTopPoint,
  createCustomLabelPosition
} from '../../utils/game-engine/label-position';
import MapResetRotate from './map-reset-rotate';
import { Task } from '../../types/entities/task';
import ZoneBackButton from '../zone-back-button';
import { GameContext } from '../../contexts/game';
import { TeamContext } from '../../contexts/team';
import usePrevious from '../../hooks/use-previous';
import { PlayerContext } from '../../contexts/player';
import GameStatusOverlay from '../game-status-overlay';
import { isNetworkOnline } from '../../utils/indicator';
import { useGameTask } from '../../hooks/use-game-task';
import { zoneAreasStyle } from '../../utils/map-helper';
import { TradeStateContext } from '../../contexts/trade';
import { useGameEvent } from '../../hooks/use-game-event';
import ListenerManager from '../../utils/listener-manager';
import { useTimerActions } from '../../hooks/use-game-timer';
import { TaskContent } from '../../features/task/task-content';
import { OnDemandTask } from '../../features/task/on-demand-task';
import { GameDocumentContext } from '../../contexts/game-document';
import { useGeolocationTracking } from '../../hooks/useGeolocation';
import { MapIllustrationContext } from './map-illustration-context';
import { UpdatePlayerStateAsync } from '../../utils/game-engine/base';
import { GetResourceValue } from '../../utils/game-document/resources';
import { PlayerPosition } from '../../features/in-game/player-position';
import { moveBackTaskFromTop } from '../../utils/game-engine/task-position';
import { Zone, ZoneAssetId } from '../../types/game-document/entities/zone';
import { useGamePlayerPosition } from '../../hooks/use-game-player-position';
import { getPlayerLocationStatus } from '../../utils/game-document/location';
import { TradeNotificationDialog } from '../dialog/trade-notification-dialog';
import { AddArea, AddTask, GetTaskById } from '../../utils/game-engine/assets';
import { pushQueueCommandLocalstorage } from '../../utils/game-engine/queue-handler';

export type LogoArea = '' | 'zone' | 'task';

export const Map = () => {
  const mapElement = useRef<HTMLDivElement>(null);
  const map = useContext(MapContext);
  const [trade] = useContext(TradeStateContext);
  const [game, , hubConnection] = useContext(GameContext);
  const [gameDocument] = useContext(GameDocumentContext);
  const [team] = useContext(TeamContext);
  const mapIllustration = useContext(MapIllustrationContext);
  const [player, setPlayer] = useContext(PlayerContext);

  const [showLocation, setShowLocation] = useState<boolean>(true);
  const [isStreetView, setIsStreetView] = useState<boolean>(true);
  const [illustrationMapLoaded, setIllustrationMapLoaded] =
    useState<boolean>(false);

  const inGameSetting = useMemo(
    () =>
      game.gameState?.runtime?.inGame ??
      gameDocument.gameDocument?.settings.inGame,
    [
      game.gameState?.runtime?.inGame,
      gameDocument.gameDocument?.settings.inGame
    ]
  );

  const objectLineColor: string | Color = gameDocument?.theme?.colors?.find(
    (x) => x.type === 'Info'
  )?.color ?? [255, 85, 39];

  const [vectorLayer] = useState(
    () =>
      new VectorLayer({
        source: new VectorSource(),
        style: (feature) => zoneAreasStyle(feature, objectLineColor)
      })
  );

  const prevPlayerLocation = usePrevious(player.playerState?.location);
  const prevPlayerCoordinates = usePrevious(
    cloneDeep(player.playerState?.coordinates)
  );
  const playerCode = player?.playerState?.code;
  const teamCode = player.playerState?.teamCode;
  const gameDoc = gameDocument?.gameDocument;

  const currentMap = useMemo(() => {
    const location = player.playerState?.location;
    let mapEntity: MapEntity | undefined = undefined;
    let areas: AreaEntity[] = [];
    if (!location) return undefined;

    areas = GetDocumentAreas(gameDoc);

    if (location === 'world-map' || location === '') {
      const mapAsId = gameDoc?.rules.worldMap.mapAssId;
      mapEntity = gameDoc?.assets.maps?.find((m) => m.id === mapAsId);
    } else {
      const zone = gameDoc?.rules.worldMap.zones.find(
        (m) => m.zoneAssId === location
      );
      mapEntity = gameDoc?.assets.maps?.find((m) => m.id === zone?.mapAssId);
    }

    setPlayer((prev) => {
      if (!prev.playerState) return prev;

      let response = prev.playerState;
      areas.forEach((area) => {
        response = AddArea(prev.playerState!, {
          id: area.id,
          isVisible: area.isVisible
        });
      });

      return UpdatePlayerStateAsync(prev, response);
    });

    return mapEntity;
  }, [player?.playerState?.location, gameDoc]);

  const currentMapContext = useMemo(() => {
    return currentMap?.type === 'illustrated' ? mapIllustration : map;
  }, [currentMap?.type, map, mapIllustration]);

  const {
    addMine,
    addAllPlayer,
    addAllPlayerOnIllustration,
    updatePlayerOverlayContent,
    updatePlayerOverlayPosition
  } = useGamePlayerPosition({ mapType: currentMap?.type ?? 'googleMaps' });

  const { executeAreaEvent, executeMapOrZoneEvent } = useGameEvent();

  //#region Update my position in map
  const { accuracy } = useGeolocationTracking({
    projection: map.getView().getProjection(),
    playerCode: player?.playerState?.code,
    enabled: !!player?.playerState?.code,
    onFix: (coord) => {
      if (!player?.playerState?.code) return;
      const { latitude, longitude } = coord;
      const lonLat = toLonLat([longitude!, latitude!]);
      setPlayer((prev) =>
        UpdatePlayerStateAsync(prev, UpdatePlayerCoordinate(prev, lonLat))
      );
      updatePlayerOverlayPosition(player.playerState.code, lonLat);
    }
  });

  const {
    getMapTasks,
    openTaskContent,
    isTaskAccessible,
    removeTaskDistance,
    populateTaskOverlay,
    changeTaskIconToComplete,
    changeTaskIconToInComplete
  } = useGameTask({
    team,
    mapType: currentMap?.type ?? 'googleMaps',
    accuracy
  });

  const addTaskHandler = async (message: any) => {
    message?.listIds?.forEach((taskId: string) => {
      const existingTask = GetTaskById(player, taskId);
      if (!existingTask) {
        const newTask: Task = {
          id: taskId,
          isVisible: true
        };

        setPlayer((prev) => {
          const response = AddTask(prev, newTask);
          return UpdatePlayerStateAsync(prev, response);
        });
      }
    });
  };

  const locationUpdateHandler = async (message: any) => {
    if (message?.id === 'world-map') {
      setPlayer((prev) => {
        const response = NavigateToWorldMap(prev);
        return UpdatePlayerStateAsync(prev, response);
      });
    } else {
      setPlayer((prev) => {
        const response = PlayerNavigateToZone(prev, message?.id);
        return UpdatePlayerStateAsync(prev, response);
      });
    }
  };

  const openTaskHandler = async (message: any) => {
    setPlayer((prev) => {
      let response = OpenTask(prev, message.id);
      return UpdatePlayerStateAsync(prev, response);
    });
    openTaskContent(message.id);
  };

  const openRandomTaskByListener = useCallback(
    (message: any) => {
      let taskIds = (message?.listIds as string[]) ?? [];

      if (message.excludeCompletedTasks) {
        const tasks = player.playerState?.tasks ?? [];
        const completedTaskIds = new Set(
          tasks
            .filter((task) => task.status === 'completed')
            .map((task) => task.id)
        );

        taskIds = taskIds.filter((id) => !completedTaskIds.has(id));
      }

      let tasks = player.playerState?.tasks!;

      let randomTask = sampleSize(
        difference(taskIds, tasks.map((task) => task.id) || []),
        1
      );

      for (const taskId of randomTask as string[]) {
        openTaskContent(taskId);
      }
    },
    [player.playerState?.tasks]
  );

  const getMapType = () => {
    if (!currentMap) return;

    if (currentMap?.type === 'illustrated') {
      map.setTarget(undefined);
      const mapImage = GetResourceValue(gameDoc!, currentMap.imageResId!, '');
      const img = new Image();
      img.src = mapImage;
      img.onload = () => {
        const extent = [0, 0, img?.width!, img?.height!];
        const projection = new Projection({
          code: 'xkcd-image',
          units: 'pixels',
          extent: extent
        });

        mapIllustration.setView(
          new View({
            projection: projection,
            center: getCenter(extent),
            extent,
            zoom: 1
          })
        );

        const imageLayer = new ImageLayer({
          source: new Static({
            attributions: '',
            url: mapImage,
            projection: projection,
            imageExtent: extent
          })
        });

        let layers = mapIllustration.getAllLayers();
        mapIllustration.setTarget(mapElement.current as HTMLElement);
        layers[0] = imageLayer;
        layers[1] = vectorLayer;
        mapIllustration.setLayers(layers);
        setIllustrationMapLoaded(true);
      };
    } else {
      mapIllustration.setTarget(undefined);
      map.setTarget(mapElement.current as HTMLElement);

      const resolvedType =
        currentMap?.type === 'openStreetMap'
          ? 'googleMaps'
          : (currentMap?.type ?? 'googleMaps');
      const adapter = new MapAdapter({ type: resolvedType } as MapEntity);
      const layers = map.getAllLayers();
      if (layers.length > 0) {
        layers[0] = adapter.baseLayer;
        map.setLayers(layers);
      }

      if (!map.getLayers().getArray().includes(vectorLayer)) {
        map.addLayer(vectorLayer);
      }
    }
  };

  const mapTypeOption = () => {
    const resolvedType =
      currentMap?.type === 'openStreetMap' || currentMap?.type === undefined
        ? 'googleMaps'
        : currentMap?.type;
    const adapter = new MapAdapter({ type: resolvedType } as MapEntity);
    return adapter.baseLayer;
  };

  const zoomOnPlayerLocationChanged = (location: string) => {
    if (location === 'world-map') {
      let mapAsset: MapEntity | undefined = getMapAsset(location);
      map.getView().setZoom(mapAsset?.zoomLevel ?? 17);
      centerMap(mapAsset?.latitude!, mapAsset?.longitude!, false);
    }
  };

  const getMapAsset = (location: string) => {
    if (location === 'world-map' || location === '') {
      const mapAssId = gameDoc?.rules.worldMap.mapAssId;
      return gameDoc?.assets?.maps?.find((m) => m.id === mapAssId);
    } else {
      //check mapAssId by zone
      const zone = gameDoc?.rules.worldMap.zones.find((m) => {
        return m.zoneAssId === location;
      });
      return gameDoc?.assets?.maps?.find((m) => m.id === zone?.mapAssId);
    }
  };

  const navigateToDefaultWorldmapTask = () => {
    if (player?.playerState?.location === 'world-map') {
      const tasks = getMapTasks();
      if (tasks && tasks?.length > 0) {
        centerTask(tasks[0]);
      } else {
        // if game doesn't have a task, set center of the map from mapAsset lat-long
        if (gameDocument?.gameDocument?.assets.maps) {
          let worldMap = gameDocument.gameDocument?.assets?.maps?.find(
            (m) => m.id === gameDocument.gameDocument?.rules.worldMap.mapAssId
          );

          centerMap(worldMap?.latitude!, worldMap?.longitude!, false);
        }
      }
    }
  };

  const trackAreaEventAction = async (location: string) => {
    let areas: AreaEntity[] = [];

    if (location === 'world-map') {
      areas = GetAreasWorldMap(gameDoc);
    } else {
      areas = GetAreasByZoneId(gameDoc, location);
    }

    for (const area of areas) {
      const currentCoordinate = fromLonLat([
        player.playerState?.coordinates!.longitude!,
        player.playerState?.coordinates!.latitude!
      ]);
      const prevCoordinate = fromLonLat([
        prevPlayerCoordinates?.longitude!,
        prevPlayerCoordinates?.latitude!
      ]);

      const geoJson = new GeoJSON().readFeature(area.boundary);
      if (geoJson instanceof Feature) {
        const geometry = geoJson.getGeometry();

        if (geometry) {
          const prevLocation = geometry.intersectsCoordinate(prevCoordinate);
          const currentLocation =
            geometry.intersectsCoordinate(currentCoordinate);

          if (!prevLocation && currentLocation) {
            await executeAreaEvent(area, 'onEntry');
          } else if (prevLocation && !currentLocation) {
            await executeAreaEvent(area, 'onExit');
          }
        }
      }
    }
  };

  const zoomToZone = (zoneId: string) => {
    let source = vectorLayer.getSource();
    if (!zoneId) return;
    if (source) {
      let features = source.getFeatures();

      const fitOptions = { duration: 1000 };
      const zone = GetZoneById(gameDoc, zoneId);
      const zoneRules = GetZoneRulesById(gameDoc, zoneId);
      if (zone) {
        features.forEach((ft) => {
          if (ft.getId() === zone.boundary?.id) {
            let geo = ft.getGeometry() as SimpleGeometry;
            ft.unset('modifyGeometry', true);
            if (geo) {
              const tileLayer = mapTypeOption();
              map.setLayers([tileLayer]);
              map.getView().fit(geo, fitOptions);
            }
          }
        });
      }
    }
  };

  const centerMap = (latitude: number, longitude: number, task: boolean) => {
    if (task) {
      map.getView().setCenter([latitude, longitude]);
    } else {
      map.getView().setCenter(fromLonLat([longitude, latitude]));
    }
  };

  const centerTask = (task: TaskEntity) => {
    //simplify : get first task
    const gameDocTask = task?.boundary?.geometry as any;
    centerMap(gameDocTask?.coordinates[0], gameDocTask?.coordinates[1], true);
  };

  const executeLocationEventAction = async (location: string) => {
    // need a delay because Location Event Action has onEnter Action
    // will be the special case for navigate to another location set in onEnter Action
    setTimeout(async () => {
      await executeMapOrZoneEvent(prevPlayerLocation, location);
    }, 1000);
  };

  const mapEventHandler = useCallback(() => {
    removeTaskDistance();
    moveBackTaskFromTop();
  }, [removeTaskDistance, moveBackTaskFromTop]);

  const mapIllustrationEventHandler = useCallback(
    (evt: MapBrowserEvent<any>) => {
      removeTaskDistance();
      const taskList = document.querySelectorAll(
        '[id^="otherPlayersContainer-"]'
      );
      taskList.forEach((list) => {
        list
          .querySelector('[id^="otherPlayersList-"]')
          ?.classList.add('d-none');
        list.querySelector('div.text-danger')?.classList.add('d-none');
        list.querySelector('span')?.classList.remove('d-none');
        list.classList.remove('expanded');
        list.classList.add('cursor-pointer');
      });

      mapIllustration.forEachFeatureAtPixel(evt.pixel, function (feature) {
        const boundaryId = feature.getId();
        if (boundaryId) {
          const task = gameDocument.gameDocument?.assets?.tasks?.find(
            (x) => x.boundary?.id === boundaryId
          );
          if (task) {
            openTaskContent(task.id);
          }
        }
      });
    },
    [mapIllustration, openTaskContent]
  );

  const centerWorldMap = () => {
    if (gameDocument?.gameDocument?.assets.maps) {
      let worldMap = gameDocument.gameDocument?.assets?.maps?.find(
        (m) => m.id === gameDocument.gameDocument?.rules.worldMap.mapAssId
      );

      map.getView().setZoom(worldMap?.zoomLevel ?? 17);
      map
        .getView()
        .setCenter(
          fromLonLat([
            worldMap?.longitude ?? 152.9043310817896,
            worldMap?.latitude ?? -27.681927396540104
          ])
        );
    }
  };

  const togglePlayerPosition = () => {
    if (
      player.playerState?.location !== undefined &&
      player.playerState.location !== ''
    ) {
      let mapId: string = '';
      if (player.playerState.location === 'world-map') {
        mapId = gameDocument.gameDocument?.rules.worldMap.mapAssId!;
      } else {
        mapId = gameDocument.gameDocument?.rules.worldMap.zones.find(
          (x) => x.zoneAssId === player.playerState?.location
        )?.mapAssId!;
      }
      const mapType = gameDocument.gameDocument?.assets.maps?.find(
        (x) => x.id === mapId
      )?.type;
      if (
        mapType === 'openStreetMap' ||
        mapType === 'googleMaps' ||
        mapType === 'satelliteMap'
      )
        setShowLocation(true);
      else setShowLocation(false);
    } else {
      setShowLocation(false);
    }
  };

  /**
   *
   * @param e Street view option true/false
   * retrieve tile layer from adapter
   */
  const onViewChange = (e: SwitchChangeEvent) => {
    const mapTypeChange = e.target.value;
    setIsStreetView(mapTypeChange);

    const mapType = mapTypeChange ? 'googleMaps' : 'satelliteMap';
    const adapter = new MapAdapter({ type: mapType } as MapEntity);
    const tileLayer = adapter.baseLayer;

    const layers = map.getAllLayers();
    if (layers.length > 0) {
      layers[0] = tileLayer;
      map.setLayers(layers);
    } else {
      map.setLayers([tileLayer]);
    }

    map.getView();
  };

  const mapImages = useMemo(() => {
    if (gameDoc) {
      const location = player.playerState?.location;
      if (location === 'world-map' || location === '') {
        return (
          gameDoc.assets?.mapImages?.filter(
            (item) => item.mapId === gameDoc.rules?.worldMap?.mapAssId!
          ) ?? []
        );
      } else {
        return gameDoc.assets?.mapImages?.filter(
          (item) =>
            item.mapId ===
              gameDoc.rules.worldMap.zones.find(
                (zone: ZoneAssetId) => zone.zoneAssId === location
              )?.mapAssId || ''
        );
      }
    }

    return [];
  }, [gameDoc, player.playerState?.location]);

  const imageList = useMemo(() => {
    const result: any[] = [];

    if (mapImages && mapImages?.length > 0) {
      mapImages.forEach((image) => {
        if (image.imageResId) {
          const url = GetResourceValue(gameDoc!, image.imageResId, '');
          result.push({
            id: `image-${image.id}`,
            url,
            opacity: image.opacity ?? 1,
            extent: image.extent,
            isVisible: image.isVisible
          });
        }
      });
    }

    return result;
  }, [mapImages, gameDoc, currentMap]);

  useEffect(() => {
    const layers = currentMapContext.getAllLayers();
    const existingImageLayers = layers.filter((layer) =>
      layer.get('id')?.startsWith('image-')
    );
    existingImageLayers.forEach((layer) =>
      currentMapContext.removeLayer(layer)
    );

    if (!currentMapContext) return;
    if (currentMap?.type === 'illustrated' && !illustrationMapLoaded) return;

    if (!imageList || imageList.length === 0) return;

    const imageLayers: ImageLayer<ImageStatic>[] = [];

    // wait for all images to load before adding layers
    const promises = imageList.map(
      (imgData) =>
        new Promise<void>((resolve) => {
          const { id, url, extent, opacity = 1, isVisible } = imgData;

          const image = new Image();
          image.src = url;
          image.onload = () => {
            const layer = new ImageLayer({
              source: new ImageStatic({
                url: url,
                imageExtent: extent ?? [0, 0, 0, 0]
              }),
              properties: { id },
              opacity,
              visible: isVisible
            });

            imageLayers.push(layer);
            resolve();
          };

          image.onerror = () => {
            console.warn(`Failed to load image: ${url}`);
            resolve();
          };
        })
    );

    // once all images are loaded, update the layer order
    Promise.all(promises).then(() => {
      if (imageLayers.length > 0) {
        const tileLayer = layers[0];
        const otherLayers = layers
          .slice(1)
          .filter((layer) => !layer.get('id')?.startsWith('image-'));

        // new order: tile → images → others
        currentMapContext.setLayers([
          tileLayer,
          ...imageLayers,
          ...otherLayers
        ]);
      }
    });
  }, [
    currentMap?.type,
    currentMapContext,
    illustrationMapLoaded,
    player.playerState?.location,
    imageList.map((i) => i.id + i.url.substring(0, 20)).join('')
  ]);

  useEffect(() => {
    if (!currentMap?.type) return;
    if (
      currentMap.type === 'openStreetMap' ||
      currentMap.type === 'googleMaps' ||
      currentMap.type === 'satelliteMap'
    ) {
      map.on('singleclick', mapEventHandler);
    } else {
      mapIllustration.on('singleclick', mapIllustrationEventHandler);
    }

    return () => {
      if (
        currentMap.type === 'openStreetMap' ||
        currentMap.type === 'googleMaps' ||
        currentMap.type === 'satelliteMap'
      ) {
        map.un('singleclick', mapEventHandler);
      } else {
        mapIllustration.un('singleclick', mapIllustrationEventHandler);
      }
    };
  }, [
    map,
    currentMap?.type,
    mapIllustration,
    mapEventHandler,
    mapIllustrationEventHandler
  ]);

  useEffect(() => {
    const source = vectorLayer.getSource();
    if (!source) return;
    if (!gameDoc) return;
    source.clear();

    const location = player.playerState?.location;
    if (!location) return;
    let areaIdsInCurrentLocation: string[] = [];

    if (location === 'world-map') {
      areaIdsInCurrentLocation = GetAreasWorldMap(gameDoc).map(
        (area) => area.id
      );
    } else {
      areaIdsInCurrentLocation = GetAreasByZoneId(gameDoc, location).map(
        (area) => area.id
      );
    }

    const areas = player.playerState?.areas ?? [];
    areas
      .filter((area) => areaIdsInCurrentLocation.includes(area.id ?? ''))
      .forEach((area) => {
        const entity = gameDoc && area.id && GetAreaById(gameDoc, area.id);
        if (entity && entity.boundary) {
          const feature = new GeoJSON().readFeature(entity.boundary) as Feature;
          feature.set('isVisible', area.isVisible);
          source.addFeature(feature);

          const id = entity.boundary.id ?? '';
          const labelOverlay = currentMapContext.getOverlayById(id);

          if (area.isVisible && entity.isTitleVisible && entity.boundary) {
            if (!labelOverlay) {
              const title = GetResourceValue(
                gameDoc,
                entity.titleResId ?? '',
                ''
              );

              const labelElement = createCustomLabelPosition(id, title);

              const overlay = new Overlay({
                id,
                position: getTopPoint(
                  (feature.getGeometry() as Polygon).getCoordinates()[0] as any
                ),
                positioning: 'center-center',
                element: labelElement,
                stopEvent: false
              });

              currentMapContext.addOverlay(overlay);
            }
          } else {
            if (labelOverlay) currentMapContext.removeOverlay(labelOverlay);
          }
        }
      });

    const renderZone = (zone: Zone) => {
      const feature = new GeoJSON().readFeature(zone.boundary) as Feature;
      feature.set('isVisible', zone.isVisible);
      source.addFeature(feature);

      const id = zone.boundary?.id ?? '';
      const labelOverlay = currentMapContext.getOverlayById(id);

      if (zone.isVisible && zone.isTitleVisible && zone.boundary) {
        const id = zone.boundary.id ?? '';
        const title = GetResourceValue(gameDoc, zone.titleResId ?? '', '');

        const labelElement = createCustomLabelPosition(id, title);

        const overlay = new Overlay({
          id,
          position: getTopPoint(
            (feature.getGeometry() as Polygon).getCoordinates()[0] as any
          ),
          positioning: 'center-center',
          element: labelElement,
          stopEvent: false
        });

        currentMapContext.addOverlay(overlay);
      } else {
        if (labelOverlay) currentMapContext.removeOverlay(labelOverlay);
      }
    };

    if (location === 'world-map') {
      const zoneIds = gameDoc.rules?.worldMap?.zones?.map(
        (zone) => zone.zoneAssId
      );

      if (zoneIds) {
        const worldZones = GetZonesByIds(gameDoc, zoneIds);

        worldZones.forEach((zone) => {
          zone.boundary && renderZone(zone);
        });
      }
    } else {
      const zone = gameDoc?.rules.worldMap.zones.find(
        (m) => m.zoneAssId === location
      );
      const zoneEntity = gameDoc?.assets.zones?.find(
        (m) => m.id === zone?.zoneAssId
      );

      zoneEntity &&
        zoneEntity.isOutlineVisibleWhenPlayerEnter &&
        zoneEntity.boundary &&
        renderZone(zoneEntity);
    }
  }, [
    gameDoc,
    vectorLayer,
    player.playerState?.location,
    JSON.stringify(player.playerState?.areas)
  ]);

  useEffect(() => {
    //for GPS only, set default center of the worldmap,
    //so the first render map directly to current game position
    centerWorldMap();
  }, [map]);

  //#region Trigger add my position in map
  useEffect(() => {
    if (!currentMap?.type) return;
    if (map && player && currentMap.type !== 'illustrated') {
      addMine();
    }
  }, [map, player, currentMap?.type, addMine]);

  //#region Trigger update my profile (avatar, name, team, and score)
  useEffect(() => {
    if (player.playerState) {
      const { code, avatarImage, name, score, lastUpdated } =
        player.playerState!;
      if (
        code !== undefined &&
        avatarImage !== undefined &&
        name !== undefined &&
        score !== undefined
      ) {
        const playerStatus = getPlayerLocationStatus(lastUpdated);
        updatePlayerOverlayContent(
          code,
          avatarImage,
          name,
          score,
          playerStatus,
          team.teamState?.name
        );
      }
    }
  }, [player.playerState, team.teamState?.name, updatePlayerOverlayContent]);

  //#region Update players icon based on runtime ingame setting
  useEffect(() => {
    if (!currentMap?.type) return;
    const playerContainer = document.querySelectorAll(
      `[id^="playerContainer-"]`
    );
    const myCode = player.playerState?.code;
    playerContainer.forEach((player) => {
      const playerCode = player.id.replaceAll('playerContainer-', '');

      // swicth show other player setting
      if (myCode !== playerCode) {
        inGameSetting?.showOtherPlayerOrTeamOnMap
          ? player.classList.remove('d-none')
          : player.classList.add('d-none');
      }

      // swicth show player score setting
      const detailTaskContainer = player.querySelector('[id^="playerScore-"]');
      if (detailTaskContainer) {
        inGameSetting?.showPlayerScoreOnMap
          ? detailTaskContainer.classList.remove('d-none')
          : detailTaskContainer.classList.add('d-none');
      }

      // swicth click to show player detail setting
      const playerProfile = player.querySelector(
        '[class^="profile-container"]'
      );
      if (playerProfile) {
        const playerContainer = player as HTMLDivElement;

        // Use the stored click handler if it exists
        const existingHandler = (playerContainer as any)._clickHandler;
        if (existingHandler) {
          // Remove existing listener first
          playerContainer.removeEventListener('click', existingHandler);

          if (inGameSetting?.showPlayerDetailByClickOnMap) {
            playerContainer.addEventListener('click', existingHandler);
          } else {
            // collapsed all player if show setting is off
            if (playerContainer.classList.contains('expanded')) {
              playerContainer.classList.remove('expanded');
              playerProfile.classList.add('hidden');
              playerProfile.classList.remove('visible');
            }
          }
        }
      }
    });

    if (currentMap.type === 'illustrated') {
      const otherPlayersContainer = document.querySelectorAll(
        `[id^="otherPlayersContainer-"]`
      );
      otherPlayersContainer.forEach((other) => {
        inGameSetting?.showOtherPlayerOrTeamOnMap
          ? other.classList.remove('d-none')
          : other.classList.add('d-none');
      });
    }
  }, [
    currentMap?.type,
    player.playerState?.code,
    inGameSetting?.showPlayerScoreOnMap,
    inGameSetting?.showOtherPlayerOrTeamOnMap,
    inGameSetting?.showPlayerDetailByClickOnMap
  ]);

  useLayoutEffect(() => {
    if (!player.playerState?.location) return;
    populateTaskOverlay();
  }, [player.playerState?.location, populateTaskOverlay]);

  const { stop: stopTimer } = useTimerActions();

  useLayoutEffect(() => {
    if (!player.playerState?.location) return;

    togglePlayerPosition();
    zoomToZone(player.playerState?.location);
    setIllustrationMapLoaded(false);
    getMapType();
    navigateToDefaultWorldmapTask();
    executeLocationEventAction(player.playerState.location);
    zoomOnPlayerLocationChanged(player.playerState.location);

    return () => {
      let timerId = '';
      if (player.playerState?.location === 'world-map') {
        timerId = gameDoc?.rules.worldMap.timerAssId ?? '';
      } else {
        timerId =
          gameDoc?.rules.worldMap.zones.find(
            (z) => z.zoneAssId === player.playerState?.location
          )?.timerAssId ?? '';
      }

      stopTimer(timerId);
    };
  }, [player.playerState?.location]);

  //#region Trigger add and update other players
  useEffect(() => {
    if (
      game.gameState &&
      currentMap?.type !== 'illustrated' &&
      inGameSetting?.showOtherPlayerOrTeamOnMap
    ) {
      addAllPlayer();
    }
  }, [
    game.gameState,
    currentMap?.type,
    inGameSetting?.showOtherPlayerOrTeamOnMap,
    addAllPlayer
  ]);

  useEffect(() => {
    addAllPlayerOnIllustration();
  }, [
    currentMap?.type,
    game.gameState?.teams,
    game.gameState?.players,
    player.playerState?.code,
    inGameSetting?.showOtherPlayerOrTeamOnMap
  ]);

  useEffect(() => {
    if (player.playerState?.tasks) {
      player.playerState.tasks.forEach((item) => {
        if (item.status === 'incomplete') {
          changeTaskIconToInComplete(item.id!);
        }
      });
    }
  }, [player.playerState?.tasks]);

  useEffect(() => {
    if (player.playerState?.coordinates) {
      if (
        player.playerState.location !== undefined &&
        player.playerState.location !== '' &&
        !isEqual(prevPlayerCoordinates, player.playerState.coordinates)
      ) {
        trackAreaEventAction(player.playerState.location);
      }
    }
  }, [player.playerState?.coordinates, player.playerState?.location]);

  useInterval(() => {
    if (
      isNetworkOnline() &&
      hubConnection?.state === HubConnectionState.Connected
    ) {
      pushQueueCommandLocalstorage(player?.gameCode!);
    }
  }, 10000);

  // player listener
  useEffect(() => {
    if (!hubConnection || !playerCode) return;

    const handler = (content: any) => {
      new ListenerManager({
        PlayerOpenTask: (message) => openTaskHandler(message),
        PlayerLocationUpdate: (message) => locationUpdateHandler(message),
        PlayerOpenRandomTask: (message) => openRandomTaskByListener(message)
      }).listen(content);
    };

    hubConnection.on(playerCode, handler);

    return () => hubConnection.off(playerCode, handler);
  }, [hubConnection, playerCode]);

  // team listener
  useEffect(() => {
    if (!hubConnection || !teamCode) return;

    const handler = (content: any) => {
      new ListenerManager({
        TeamOpenTask: (message) => openTaskHandler(message),
        TeamRandomTask: (message) => addTaskHandler(message),
        TeamDistributeTask: (message) => addTaskHandler(message),
        TeamLocationUpdate: (message) => locationUpdateHandler(message),
        TeamOpenRandomTask: (message) => openRandomTaskByListener(message)
      }).listen(content);
    };

    hubConnection.on(teamCode, handler);

    return () => hubConnection.off(teamCode);
  }, [hubConnection, teamCode]);

  return (
    <>
      <div className={'canvas__map'} id={'map'} ref={mapElement as any}>
        {currentMap?.type !== 'illustrated' && (
          <div className={'map-view-option'}>
            <span>Street View</span>
            <Switch
              size={'small'}
              onLabel={'Street View'}
              offLabel={'Satelite View'}
              onChange={(e) => onViewChange(e)}
              checked={isStreetView}
            />
          </div>
        )}
        {showLocation && <PlayerPosition />}
      </div>

      <MapResetRotate />
      <MapResetView key={currentMap?.type} currentMap={currentMap} />

      {/* WARNING: HACK development only, for test player movement using controller
          without go outside, enable when needed, and PLEASE DISABLE AGAIN WHEN PUSH */}
      {/* <PlayerController /> */}

      {(currentMap?.type === 'openStreetMap' ||
        currentMap?.type === 'googleMaps') && <MoveMapPad />}

      {player.playerState?.location !== 'world-map' &&
        GetZoneById(gameDoc, player.playerState?.location ?? '')
          ?.isEnableBackButton && (
          <ZoneBackButton mapType={currentMap?.type ?? 'googleMaps'} />
        )}

      {player.playerState &&
        (player.playerState.openedTask ?? []).length > 0 &&
        (() => {
          const taskId = (player.playerState.openedTask ?? [])[0];
          const isAccessible = isTaskAccessible(taskId);
          return (
            <TaskContent
              id={taskId}
              key={taskId}
              mapType={currentMap?.type ?? 'googleMaps'}
              className={'map-task-content'}
              changeTaskIconToComplete={changeTaskIconToComplete}
              changeTaskIconToInComplete={changeTaskIconToInComplete}
              isAccesible={isAccessible}
            />
          );
        })()}

      {player.playerState?.openedOnDemandTask && (
        <OnDemandTask taskContentId={player.playerState?.openedOnDemandTask} />
      )}

      <GameStatusOverlay />

      {trade.showNotification && <TradeNotificationDialog />}
    </>
  );
};
